import { useState } from 'react';

import { useToast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';

import { SecretCredentialView, accessKeyQueryKeys, useAccessKeyList, useDeleteAccessKey } from '@codes/access-keys';

export const useAccessKeys = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [selectedKey, setSelectedKey] = useState<SecretCredentialView | null>(null);
    const [newlyCreatedCredential, setNewlyCreatedCredential] = useState<{
        accessKey: string;
        secretKey: string;
    } | null>(null);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [keyToDelete, setKeyToDelete] = useState<SecretCredentialView | null>(null);

    const keysQuery = useAccessKeyList();
    const deleteMutation = useDeleteAccessKey();
    const handleCreate = () => {
        setDialogMode('create');
        setSelectedKey(null);
        setIsFormDialogOpen(true);
    };

    const handleEdit = (key: SecretCredentialView) => {
        setDialogMode('edit');
        setSelectedKey(key);
        setIsFormDialogOpen(true);
    };

    const handleDelete = (key: SecretCredentialView) => {
        setKeyToDelete(key);
        setIsDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!keyToDelete?.id) {
            return;
        }

        try {
            await deleteMutation.mutateAsync({ id: keyToDelete.id });
            toast({
                title: 'Secret credential deleted successfully',
            });

            queryClient.invalidateQueries({ queryKey: accessKeyQueryKeys.all });
            setIsDeleteDialogOpen(false);
            setKeyToDelete(null);
        } catch (error) {
            toast({
                title: 'Failed to delete secret credential',
                variant: 'destructive',
            });
        }
    };

    const handleFormSuccess = (credential?: { accessKey: string; secretKey: string }) => {
        queryClient.invalidateQueries({ queryKey: accessKeyQueryKeys.all });

        if (credential) {
            setNewlyCreatedCredential(credential);
            setIsSuccessDialogOpen(true);
        }
    };

    return {
        keysData: keysQuery.data,
        isKeysLoading: keysQuery.isLoading,
        isFormDialogOpen,
        setIsFormDialogOpen,
        dialogMode,
        selectedKey,
        isSuccessDialogOpen,
        setIsSuccessDialogOpen,
        newlyCreatedCredential,
        isDeleteDialogOpen,
        setIsDeleteDialogOpen,
        keyToDelete,
        isDeleting: deleteMutation.isPending,
        handleCreate,
        handleEdit,
        handleDelete,
        confirmDelete,
        handleFormSuccess,
    };
};
